/**
 * Sender identity — deterministic per-person colour + initials.
 *
 * Every surface that renders a person in chat (message list, dock widget,
 * reply previews) derives their colour from this one place, so the same
 * person is the same colour everywhere. Group chats become scannable
 * without anyone having to configure a thing.
 *
 * The palette is hand-picked rather than hue-rotated: twelve tones that sit
 * at roughly equal perceived lightness on the near-black chat canvas and
 * stay clear of the rose accent used for "you" and for interactive chrome.
 */

const SENDER_PALETTE = [
  "#ff9e7a", // coral
  "#f7b955", // amber
  "#d8cf5a", // citron
  "#8fd67f", // leaf
  "#5ad2b8", // aqua
  "#63c9e8", // sky
  "#8aa8ff", // periwinkle
  "#a68cff", // violet
  "#d18cff", // orchid
  "#ff8fd6", // magenta
  "#e0b48a", // sand
  "#8fd4c4", // sage
] as const;

/** FNV-1a — small, fast, well-distributed for short id strings. */
function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Stable display colour for a person. Same id always returns the same tone. */
export function senderColor(userId: string | undefined): string {
  if (!userId) return SENDER_PALETTE[0];
  return SENDER_PALETTE[hashString(userId) % SENDER_PALETTE.length];
}

/** Up to two letters for avatar fallbacks: "Ada Lovelace" → "AL", "ron" → "RO". */
export function initialsOf(name: string | undefined): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "?";
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}
